import { useCallback, useEffect, useState } from 'react';

import { Button } from '@components/button';
import { HtmlContent } from '@components/html-content';
import { IconShadowContainer } from '@components/icon-shadow-container';
import { IconBrandJourneysGradient } from '@components/icons/brand-journeys-gradient';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconSwap } from '@components/icons/swap';
import { InfoBox } from '@components/info-box';
import { IntegrationIcon } from '@components/integrations/common';
import { hideModal, Modal, useModal } from '@components/modal';
import { PrefixedPostfixedTextField } from '@components/prefixed-postfixed-text-field';
import { Stack } from '@components/stack';
import { StepperVertical } from '@components/stepper-vertical';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useScopedTranslate } from '@locale/translate';

import {
  useConnectMarketplaceTemplate,
  useInvalidateQueriesAfterConnect,
  useReconnectIntegration,
} from 'api/app-marketplace/hooks';
import {
  initiateOAuthAuthorize,
  MarketplaceTemplate,
  MarketplaceTemplateAuthBaseUrl,
  MarketplaceTemplateAuthInfoItem,
} from 'api/app-marketplace/requests';

export type ConnectModalProps = {
  template: MarketplaceTemplate;
  authentication?: MarketplaceTemplate['authentications'][number];
  isEdit?: boolean;
  journeyActionId?: string;
  onSuccess?: () => void;
};

export const useConnectModal = () => useModal(ConnectModal, { variant: 'REGULAR' });

export const ConnectModal = ({
  template,
  authentication,
  isEdit,
  journeyActionId,
  onSuccess,
}: ConnectModalProps) => {
  const { t } = useScopedTranslate(locale);
  const authentications = template.authentications;
  const selectedAuthentication = authentication ?? authentications[0];
  const info = selectedAuthentication?.info?.info ?? [];
  const isOAuthConnect = selectedAuthentication?.authType === 'oauth_connect';

  const initialValues: Record<string, string> = {};
  if (isEdit && template.connectionDetails) {
    const editableFields = new Set<string>();
    for (const item of info) {
      if (item.type === 'input' && 'field' in item && item.field) {
        editableFields.add(item.field);
      } else if (item.type === 'base-url') {
        editableFields.add('baseUrl');
      }
    }

    Object.entries(template.connectionDetails).forEach(([key, value]) => {
      if (key === 'connectionVariables' && typeof value === 'object' && value !== null) {
        Object.entries(value as Record<string, string>).forEach(([varKey, varValue]) => {
          if (typeof varValue === 'string') {
            initialValues[varKey] = varValue;
          }
        });
      } else if (editableFields.has(key) && typeof value === 'string') {
        initialValues[key] = value;
      }
    });
  }

  const [values, setValues] = useState<Record<string, string>>(initialValues);
  const connectMutation = useConnectMarketplaceTemplate();
  const reconnectMutation = useReconnectIntegration(template.shortId);
  const invalidateQueriesAfterConnect = useInvalidateQueriesAfterConnect(template.shortId);

  const handleChange = (field: string, value: string) => {
    if (!field) return;
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleOAuthResult = useCallback(
    (success: boolean) => {
      if (success) {
        invalidateQueriesAfterConnect();
        onSuccess?.();
        hideModal();
      }
    },
    [onSuccess, invalidateQueriesAfterConnect]
  );

  useEffect(() => {
    // Method 1: Listen for postMessage from popup
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'oauth_callback') {
        handleOAuthResult(event.data.success);
      }
    };

    // Method 2: Listen for localStorage changes (fallback for when window.opener is lost)
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'oauth_callback_result' && event.newValue) {
        try {
          const result = JSON.parse(event.newValue);
          if (result.timestamp && Date.now() - result.timestamp < 10000) {
            handleOAuthResult(result.success);
            localStorage.removeItem('oauth_callback_result');
          }
        } catch {
          // Invalid JSON
        }
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [handleOAuthResult]);

  const prepareValues = () => {
    const baseUrlItem = info.find(
      (item): item is MarketplaceTemplateAuthBaseUrl => item.type === 'base-url'
    );
    const prefix = baseUrlItem?.prefix ?? '';
    const postfix = baseUrlItem?.postfix ?? '';

    const preparedValues = { ...values };
    if (preparedValues.baseUrl !== undefined) {
      let baseUrl = preparedValues.baseUrl;
      if (prefix && !baseUrl.startsWith(prefix)) {
        baseUrl = `${prefix}${baseUrl}`;
      }
      if (postfix && !baseUrl.endsWith(postfix)) {
        baseUrl = `${baseUrl}${postfix}`;
      }
      preparedValues.baseUrl = baseUrl;
    }
    return preparedValues;
  };

  const handleOAuthConnect = async () => {
    if (!selectedAuthentication) return;

    const preparedValues = prepareValues();

    const { authorizationUrl } = await initiateOAuthAuthorize({
      authenticationShortId: selectedAuthentication.shortId,
      journeyActionId,
      values: Object.keys(preparedValues).length > 0 ? preparedValues : undefined,
    });

    const width = 504;
    const height = 648;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    window.open(
      authorizationUrl,
      'oauth_popup',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );
  };

  const handleConnect = async () => {
    const preparedValues = prepareValues();

    if (isEdit && template.connectedIntegrationId) {
      await reconnectMutation.mutateAsync({
        integrationId: template.connectedIntegrationId,
        authenticationId: selectedAuthentication?.shortId,
        values: preparedValues,
        ...(journeyActionId && { journeyActionId }),
      });
    } else {
      if (!selectedAuthentication) return;
      await connectMutation.mutateAsync({
        templateId: template.shortId,
        authenticationId: selectedAuthentication.shortId,
        values: preparedValues,
        ...(journeyActionId && { journeyActionId }),
      });
    }
    invalidateQueriesAfterConnect();
    onSuccess?.();
    hideModal();
  };

  const isLoading = connectMutation.isLoading || reconnectMutation.isLoading;

  return (
    <Modal>
      <Modal.Header>
        <Stack orientation="horizontal" align="center" justify="space-between">
          <Modal.HeaderTitle title={t('connectTitle', { name: template.name })} />
          <IntegrationSwapIcons template={template} />
        </Stack>
      </Modal.Header>
      <Modal.Content>
        <Stack space={6}>
          {info.map((item: MarketplaceTemplateAuthInfoItem, index: number) => (
            <RenderInfoItem key={index} item={item} onChange={handleChange} values={values} />
          ))}
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" justify="flex-end" space={2}>
          <Button variant="secondary" text={t('cancel')} onClick={hideModal} disabled={isLoading} />
          <Button
            variant="primary-blue"
            text={t('connect')}
            onClick={isOAuthConnect ? handleOAuthConnect : handleConnect}
            disabled={!selectedAuthentication || isLoading}
          />
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

const IntegrationSwapIcons = ({ template }: { template: MarketplaceTemplate }) => {
  return (
    <Stack orientation="horizontal" align="center" space={1}>
      <IconShadowContainer icon={<IconBrandJourneysGradient />} />
      <IconSwap size={24} color="uiTrans20" />
      <IconShadowContainer icon={<IntegrationIcon src={template.logo} />} />
    </Stack>
  );
};

const RenderInfoItem = ({
  item,
  onChange,
  values,
}: {
  item: MarketplaceTemplateAuthInfoItem;
  onChange: (field: string, value: string) => void;
  values: Record<string, string>;
}) => {
  switch (item.type) {
    case 'text':
      return <Text font="regular14" color="textPrimary" text={item.text} />;
    case 'html':
      return <HtmlContent font="regular14" html={item.text?.value ?? ''} />;
    case 'list-text': {
      const list: string[] = Array.isArray(item.list) ? item.list : [];
      if (list.length === 0) return null;
      return <StepperVertical items={list.map((t) => ({ label: t }))} />;
    }
    case 'list-html': {
      const list: string[] = Array.isArray(item.list) ? item.list : [];
      if (list.length === 0) return null;
      return (
        <StepperVertical
          items={list.map((html) => ({ label: <HtmlContent font="regular14" html={html} /> }))}
        />
      );
    }
    case 'info-box':
      return (
        <InfoBox
          text={item.text ?? ''}
          variant="indigo"
          icon={<IconFormIndicatorInfo color="elementIndigo200" />}
        />
      );
    case 'info-box-html':
      return (
        <InfoBox variant="indigo" icon={<IconFormIndicatorInfo color="elementIndigo200" />}>
          <HtmlContent font="regular14" color="elementIndigo100" html={item.text?.value ?? ''} />
        </InfoBox>
      );
    case 'input':
      if (!item.field) return null;
      return (
        <TextField
          label={item.label}
          helpText={item.helpText ?? ''}
          placeholder={item.placeholder ?? ''}
          value={values[item.field] ?? ''}
          onChange={(val) => onChange(item.field, val)}
        />
      );
    case 'base-url': {
      const field = 'baseUrl';
      const prefix = item.prefix ?? '';
      const postfix = item.postfix ?? '';
      let displayValue = values[field] ?? '';
      if (prefix && displayValue.startsWith(prefix)) {
        displayValue = displayValue.slice(prefix.length);
      }
      if (postfix && displayValue.endsWith(postfix)) {
        displayValue = displayValue.slice(0, -postfix.length);
      }
      return (
        <PrefixedPostfixedTextField
          label={item.label}
          helpText={item.helpText ?? ''}
          placeholder={item.placeholder ?? ''}
          prefix={prefix}
          postfix={postfix}
          value={displayValue}
          onChange={(val) => onChange(field, val)}
          hideCopyButton
        />
      );
    }
    default:
      return null;
  }
};

const locale = {
  en: {
    connectTitle: 'Connect {name}',
    cancel: 'Cancel',
    connect: 'Connect',
  },
};
